import {Alert, createStyles, Divider, Paper, Stack, Text} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import RegistrationConfigPane from 'registry/create-bioentity/registration-config/RegistrationConfigPane';
import type {DraftRegistrationConfig} from 'registry/create-bioentity/registration-config/RegistrationConfigTypes';
import {ButtonSize} from 'ui-library/buttons/Button';

import type {ModalAction, ModalState} from './moleculeImportReducer';

const useStyles = createStyles((theme) => ({
  explainerCard: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
  },
  explainerHeader: {
    backgroundColor: theme.colors.blue[0],
    padding: theme.spacing.sm,
  },
  explainerItem: {
    display: 'flex',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
  },
  stepCircle: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue[1],
    color: theme.colors.blue[8],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
  },
}));

const EXPLAINER_STEPS = [
  {
    title: 'Spreadsheet validation',
    description: "We'll check that all data exists both in the spreadsheet and in Benchling",
  },
  {
    title: 'Entity creation',
    description:
      "We'll create each molecule in your upload as a new entity. We'll also automatically create the base molecule of any salts or solvents that reference a base molecule that doesn't exist in your organization.",
  },
  {
    title: 'Entity registration',
    description: "All successfully created entities will be registered into your organization's registry.",
  },
  {
    title: 'Error handling',
    description:
      "If molecules failed to import, you'll receive a notification and email containing a receipt of errors.",
  },
];

interface FinalizeImportPaneProps {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
}

export default function FinalizeImportPane({state, dispatch}: FinalizeImportPaneProps): JSX.Element {
  const {classes} = useStyles();

  const registrationConfig: DraftRegistrationConfig = useMemo(
    () => ({
      registryId: state.registrationConfig?.registryId ?? null,
      namingStrategy: state.registrationConfig?.namingStrategy ?? null,
      folderId: state.registrationConfig?.folderId ?? state.initialFolderId ?? null,
    }),
    [state.registrationConfig, state.initialFolderId]
  );

  const handleRegistrationConfigChange = useCallback(
    (config: DraftRegistrationConfig) => {
      if (config.registryId !== registrationConfig.registryId) {
        dispatch({type: 'SET_REGISTRY_ID', payload: {registryId: config.registryId}});
      }
      if (config.namingStrategy !== registrationConfig.namingStrategy && config.namingStrategy != null) {
        dispatch({type: 'SET_NAMING_STRATEGY', payload: {namingStrategy: config.namingStrategy}});
      }
      if (config.folderId !== registrationConfig.folderId) {
        dispatch({type: 'SET_FOLDER_ID', payload: {folderId: config.folderId}});
      }
    },
    [dispatch, registrationConfig]
  );

  return (
    <Stack spacing="lg">
      <div>
        <Text weight={600} size={16}>
          Finalize import
        </Text>
        <Text size="sm" color="dimmed">
          Review the folder and registry your molecules will be saved to.
        </Text>
      </div>

      {state.errorMessage && (
        <Alert color="red" title="Import failed">
          {state.errorMessage}
        </Alert>
      )}

      <RegistrationConfigPane
        registrationConfig={registrationConfig}
        onRegistrationConfigChange={handleRegistrationConfigChange}
        registryReadOnly={true}
        allowedLabelingStrategies={state.schemaLabelingStrategies ?? undefined}
        folderButtonSize={ButtonSize.SMALL}
        folderRequired={true}
      />

      <Divider />

      <Paper className={classes.explainerCard}>
        <div className={classes.explainerHeader}>
          <Text weight={600} size="sm">
            After you click &quot;Import&quot;
          </Text>
        </div>
        {EXPLAINER_STEPS.map((step, index) => (
          <React.Fragment key={step.title}>
            <Divider />
            <div className={classes.explainerItem}>
              <div className={classes.stepCircle}>{index + 1}</div>
              <div>
                <Text weight={600} size="sm">
                  {step.title}
                </Text>
                <Text size="xs" color="dimmed">
                  {step.description}
                </Text>
              </div>
            </div>
          </React.Fragment>
        ))}
      </Paper>
    </Stack>
  );
}
